import {
    useEffect,
    useState,
} from 'react';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import * as ProviderConnectionsService from '../services/ProviderConnectionsService';
import {
    ConnectProviderInput,
    DisconnectProviderResult,
    DnsProvider,
    ProviderConnection,
    ProviderDisconnectPreview,
    RenameProviderConnectionInput,
    RotateProviderCredentialInput,
} from '../types';

// FNP-1248 — query keys, queries and mutations for the provider-connections
// module.
//
// ⚠️ NO CREDENTIAL EVER ENTERS A QUERY KEY OR A CACHE ENTRY.
//
// React Query keys are held in memory for the life of the app, are shown in
// devtools, and are serialised by any cache persister someone adds later. The
// connect and rotate mutations therefore take their input as a mutation
// ARGUMENT and key on nothing — `useMutation` does not cache arguments, unlike
// `useQuery`. There is deliberately no `connectKeys.forCredential(...)` and no
// mutation key derived from the input.
export const providerConnectionsKeys = {
    all: ['provider-connections'] as const,
    lists: () => [...providerConnectionsKeys.all, 'list'] as const,
    list: () => [...providerConnectionsKeys.lists()] as const,
    details: () => [...providerConnectionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...providerConnectionsKeys.details(), id] as const,
    // The disconnect preview is keyed per connection and NOT cached across the
    // modal being closed and reopened — see useProviderDisconnectPreview.
    disconnectPreview: (id: string) =>
        [...providerConnectionsKeys.all, 'disconnect-preview', id] as const,
    providers: () => ['dns-providers'] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown (§9 of the module checklist).
export const PAGE_SIZE = 25;

export const useProviderConnectionsInfiniteQuery = () => {
    return useInfiniteQuery({
        queryKey: providerConnectionsKeys.list(),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ProviderConnectionsService.fetchRecords(PAGE_SIZE, pageParam),
        // ⚠️ THE ONLY END-OF-RESULTS SIGNAL IS A NULL nextToken.
        //
        // `limit` applies BEFORE filtering on this platform, so a page that
        // comes back with ZERO items and a live token is NORMAL and every match
        // may still be behind it. This branches on the token and NEVER on
        // `items.length`.
        //
        // MUST return undefined: returning null or the same token makes React
        // Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 30_000,
    });
};

export const useProviderConnectionQuery = (id?: string) => {
    return useQuery({
        queryKey: providerConnectionsKeys.detail(id ?? ''),
        // NULL for "no such connection", never undefined — React Query v5
        // rejects an undefined resolution outright ("Query data cannot be
        // undefined") and leaves the query neither success nor error. The
        // not-found state is derived from this null.
        queryFn: async (): Promise<ProviderConnection | null> => {
            if (typeof id === 'undefined' || id === '') {
                throw new Error('id is required parameter');
            }
            return await ProviderConnectionsService.fetch(id) ?? null;
        },
        enabled: Boolean(id),
        // ⚠️ SHORT. A connection's status is the thing this module is about, and
        // it changes underneath the user — they go and edit a trust policy in
        // another tab, come back, and re-validate. A long stale time would show
        // them a status from before they fixed it.
        staleTime: 10_000,
    });
};

// The provider catalog. Long stale time: the set of DNS hosts Flexi can talk to
// changes when the platform ships a new adapter, not while a form is open.
export const useDnsProvidersQuery = () => {
    return useQuery({
        queryKey: providerConnectionsKeys.providers(),
        queryFn: async (): Promise<DnsProvider[]> => {
            return await ProviderConnectionsService.fetchProviders();
        },
        staleTime: 600_000,
    });
};

/**
 * The disconnect preview.
 *
 * ⚠️ `enabled` GATES THIS ON THE MODAL BEING OPEN, and `gcTime: 0` throws the
 * answer away when it closes. Both are deliberate:
 *
 * The preview states what disconnecting would cost RIGHT NOW — a zone count and
 * a set of consequences. Serving a cached one to a modal opened ten minutes
 * later would show a count from before the tenant created three zones, on the
 * one screen where an out-of-date number is most expensive. Re-reading on every
 * open costs one query and removes a whole class of stale-confirmation bug.
 */
export const useProviderDisconnectPreviewQuery = (id?: string, enabled: boolean = false) => {
    return useQuery({
        queryKey: providerConnectionsKeys.disconnectPreview(id ?? ''),
        queryFn: async (): Promise<ProviderDisconnectPreview | null> => {
            if (typeof id === 'undefined' || id === '') {
                throw new Error('id is required parameter');
            }
            return await ProviderConnectionsService.fetchDisconnectPreview(id) ?? null;
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0,
        gcTime: 0,
    });
};

// ─── MUTATIONS ───

/**
 * Create a connection.
 *
 * ⚠️ `onSuccess` FIRES FOR AN INVALID CONNECTION TOO, AND THAT IS CORRECT.
 *
 * A first-time ASSUMED_ROLE connect resolves with status INVALID/UNREACHABLE
 * and a freshly-minted external id. The mutation SUCCEEDED — a connection was
 * created and stored. Treating a non-ACTIVE status as an error here would put
 * the flow into an error branch at the exact moment the tenant needs to be
 * shown their external id, which is the whole point of the screen.
 *
 * The list is invalidated either way, because a connection now exists.
 */
export const useConnectProviderMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: ConnectProviderInput) =>
            ProviderConnectionsService.connect(input),
        onSuccess: (connection) => {
            queryClient.invalidateQueries({queryKey: providerConnectionsKeys.lists()});
            if (connection?.id) {
                queryClient.setQueryData(
                    providerConnectionsKeys.detail(connection.id),
                    connection,
                );
            }
        },
    });
};

/**
 * Re-validate: replace the credential on an existing connection.
 *
 * Same rule as connect — a rotation that lands INVALID is a successful call
 * that recorded a failure, not a failed call. The connection is KEPT.
 */
export const useRotateProviderCredentialMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: RotateProviderCredentialInput) =>
            ProviderConnectionsService.rotateCredential(input),
        onSuccess: (connection, variables) => {
            queryClient.invalidateQueries({queryKey: providerConnectionsKeys.lists()});
            queryClient.invalidateQueries({
                queryKey: providerConnectionsKeys.detail(variables.id),
            });
            if (connection?.id) {
                queryClient.setQueryData(
                    providerConnectionsKeys.detail(connection.id),
                    connection,
                );
            }
        },
    });
};

/**
 * FNP-1304 — rename.
 *
 * ⚠️ INVALIDATES THE LIST AS WELL AS THE DETAIL, and that is the point of the
 * ticket rather than an incidental cache detail. The display name is the only
 * human-readable handle on a connection, and it is rendered in three places: the
 * connections list, the connection detail page, and the PROVIDER DROPDOWN in the
 * zones create form, which is built from the connections list
 * (`zones/forms/FormComponent.tsx`). A rename that refreshed only the detail
 * page would leave the tenant choosing between an old name and a new one for the
 * same connection in the very next form they opened.
 *
 * Unlike rotate, there is no status to interpret: a rename cannot fail
 * halfway. It either wrote the name or it threw.
 */
export const useRenameProviderConnectionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: RenameProviderConnectionInput) =>
            ProviderConnectionsService.rename(input),
        onSuccess: (connection, variables) => {
            queryClient.invalidateQueries({queryKey: providerConnectionsKeys.lists()});
            queryClient.invalidateQueries({
                queryKey: providerConnectionsKeys.detail(variables.id),
            });
            if (connection?.id) {
                queryClient.setQueryData(
                    providerConnectionsKeys.detail(connection.id),
                    connection,
                );
            }
        },
    });
};

/**
 * Disconnect.
 *
 * ⚠️ REMOVES the detail entry rather than invalidating it. An invalidate would
 * refetch a connection that no longer exists, producing a spurious error toast
 * immediately after a successful disconnect.
 */
export const useDisconnectProviderMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string): Promise<DisconnectProviderResult | undefined> =>
            ProviderConnectionsService.disconnect({id}),
        onSuccess: (_result, id) => {
            queryClient.removeQueries({queryKey: providerConnectionsKeys.detail(id)});
            queryClient.removeQueries({
                queryKey: providerConnectionsKeys.disconnectPreview(id),
            });
            queryClient.invalidateQueries({queryKey: providerConnectionsKeys.lists()});
        },
    });
};

// ─── MODULE-SCOPED SEARCH STORE ───
//
// FNP-815: the search input (TableSearchForm, inside the table component) and
// the list hook that filters on the keyword live in DIFFERENT components. A
// local useState would give each its own copy and search would silently do
// nothing. Module-scoped store; every caller subscribes.
//
// Do not replace with a local useState.
let globalKeyword = '';
const keywordListeners: Set<(keyword: string) => void> = new Set();

const notifyKeywordChange = (keyword: string) => {
    globalKeyword = keyword;
    keywordListeners.forEach(listener => listener(keyword));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a keyword set in one test leaks
// into the next.
export const resetProviderConnectionsSearch = () => {
    globalKeyword = '';
    keywordListeners.clear();
};

export const useProviderConnectionsSearch = () => {
    const [keyword, setKeyword] = useState<string>(globalKeyword);

    useEffect(() => {
        const listener = (newKeyword: string) => {
            setKeyword(newKeyword);
        };
        keywordListeners.add(listener);
        // A consumer mounting after the keyword was set must not start from ''.
        setKeyword(globalKeyword);
        return () => {
            keywordListeners.delete(listener);
        };
    }, []);

    return {
        keyword,
        handleFilterByKeyword: (newKeyword: string) => notifyKeywordChange(newKeyword),
    };
};
